// A branch's own catalogue settings, in that branch's settings — Settings › Branches, the branch selected (UC-CATALOGUE-011 A4,
// A5; D40). The two switches (BR-CATALOGUE-029) — whether a frame added at the branch starts serialised, and whether a lens the
// branch's own price-list import creates starts made to order — as the branch resolves them, each with where its value comes
// from; Change sets the branch's own value or puts the branch back on the organisation's, through the setting store (`PUT` and
// `DELETE /settings/{branch}/catalogue`, `tenancy.settings.write`: the Owner at any branch, a Branch Owner at the branches they
// own), as Pricing settings change a branch's (PricingSettings.tsx). Every role reads the values (`tenancy.settings.read`); the
// organisation's are set under Settings › Markup & fitting › Catalogue settings (`catalogue.settings.write`: Owner, Admin). An
// inactive branch's values are read only. At a practice of one store (UC-CATALOGUE-011 A6; DVN's answers of 28 Oct 2026, change 13)
// a value of the branch's own would repeat the organisation's: an Owner or Admin, who sets those, sees no panel here while the
// branch holds none, and the panel's words name no other branch.
import type { SettingSource } from "@visionx/contracts";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState, type ReactNode } from "react";
import { Link } from "react-router";
import { settingStore } from "../api/endpoints";
import { describeError } from "../api/problem";
import { useOneStore } from "../session/one-store";
import { can, canEverywhere, useSessionView } from "../session/store";
import { WRITE_BLOCK_TITLE, useWriteBlock } from "../shell/OfflineBanner";
import { Alert, ErrorAlert, Loading, Panel, Select } from "../ui/bits";
import { possessive, sourceWord } from "./PricingShared";

type Key = "framesSerialisedByDefault" | "importCreatesNonStockedLenses";
interface Switch { key: Key; label: string; help: (branch: string, oneStore: boolean) => string }
const SWITCHES: readonly Switch[] = [
  { key: "framesSerialisedByDefault", label: "Frames start serialised", help: (b) => `Whether a frame added at ${b} starts with Serialised ticked — every unit then gets its own serial when it arrives.` },
  { key: "importCreatesNonStockedLenses", label: "Lenses an import creates start made to order", help: (b, one) => (one
    ? "Whether a lens a price-list import creates holds no stock of its own."
    : `Whether a lens ${possessive(b)} own price-list import creates holds no stock of its own; an import for all branches follows the organisation's value.`) },
];
const INHERIT = "__organisation__";
const onOff = (v: unknown): string => (v === false ? "Off" : "On");

/** The `catalogue` settings at a scope — `org` or a branch — each with where it comes from (UC-TENANCY-007; every role reads them). */
const useCatalogueAt = (scope: string, enabled = true) => useQuery({ queryKey: ["settings", scope, "catalogue"], queryFn: () => settingStore.read(scope, "catalogue"), staleTime: 30_000, enabled });

/** Whether the reader sets this branch's own values: the Owner at any branch, a Branch Owner at the branches they own (D40). */
function useMaySet(branchId: string): boolean {
  const s = useSessionView();
  if (!s) return false;
  if (canEverywhere(s, "tenancy.settings.write")) return true;
  return (s.branchScopedPermissions.find((p) => p.code === "tenancy.settings.write")?.branchIds ?? []).some((id) => id === branchId);
}

export function BranchCatalogueSettingsPanel({ branch }: { branch: { id: string; name: string; status: string } }) {
  const s = useSessionView();
  const here = useCatalogueAt(branch.id);
  const maySet = useMaySet(branch.id);
  // The organisation's values, for "As the organisation (…)" — read ahead for a reader who may change the branch's.
  useCatalogueAt("org", maySet);
  const block = useWriteBlock();
  const [editing, setEditing] = useState<Key | null>(null);
  const [notice, setNotice] = useState<ReactNode | null>(null);
  const closed = branch.status !== "Active";
  const values = here.data?.data.settings;
  const where = "Settings › Markup & fitting › Catalogue settings";
  const oneStore = useOneStore();
  const orgWriter = can(s, "catalogue.settings.write");
  const own = SWITCHES.some((w) => values?.[w.key]?.source === "branch");
  // Once shown for a value of the branch's own, the panel stays while it is open, so putting that value back says it was done.
  const [shown, setShown] = useState(false);
  useEffect(() => { if (own) setShown(true); }, [own]);
  // Nothing of the store's own to show the Owner or an Admin of a practice of one store: they set the values once, for the
  // organisation (A6 step 1); a value the store holds of its own, set before, is shown with the way back (A6 step 2); a branch
  // closed since, selected, shows its values as ever, read only.
  if (oneStore && orgWriter && !closed && !own && !shown && !here.isError) return null;
  const link = orgWriter ? <Link to="/settings/markups/catalogue">{where}</Link> : where;
  return (
    <Panel title="Catalogue settings" meta={`as ${branch.name} resolves them`}
      foot={<span className="help">{oneStore
        ? <>Set by an Owner or Admin under {link}. Products already added keep their flags.</>
        : <>The organisation&apos;s values are set under {link} by an Owner or Admin; a branch&apos;s own value wins at that branch. Products already added keep their flags.</>}</span>}>
      {notice && <div aria-live="polite">{notice}</div>}
      {here.isPending && <Loading what="the catalogue settings" />}
      {here.isError && <ErrorAlert error={here.error} />}
      {values && (
        <div className="list">
          {SWITCHES.map((w) => {
            const held = values[w.key];
            const title = closed ? "The branch is inactive" : block ? WRITE_BLOCK_TITLE[block] : undefined;
            return (
              <div key={w.key} className="item" style={{ flexWrap: "wrap" }}>
                <div className="grow"><div className="primary">{w.label}</div><div className="sub">{w.help(branch.name, oneStore)}</div></div>
                <span className="small" data-setting={w.key}><b>{onOff(held?.value)}</b> · <span className="faint">{sourceWord((held?.source ?? "default") as SettingSource, branch.name)}</span></span>
                {maySet && editing !== w.key && (
                  <button className="btn sm" type="button" disabled={closed || block !== null} title={title} aria-label={`Change ${w.label}`} onClick={() => { setNotice(null); setEditing(w.key); }}>Change</button>
                )}
                {editing === w.key && held && (
                  <SwitchEditor setting={w} branch={branch} held={held} onClose={() => setEditing(null)} onSaved={(words) => { setEditing(null); setNotice(<Alert tone="ok">{words}</Alert>); }} />
                )}
              </div>
            );
          })}
        </div>
      )}
    </Panel>
  );
}

/** One switch at the branch: its own On or Off, or the organisation's again — then Save (Enter). */
function SwitchEditor({ setting, branch, held, onClose, onSaved }: { setting: Switch; branch: { id: string; name: string }; held: { value: unknown; source: SettingSource }; onClose: () => void; onSaved: (words: ReactNode) => void }) {
  const qc = useQueryClient();
  const org = useCatalogueAt("org");
  const current = held.source === "branch" ? (held.value === false ? "off" : "on") : INHERIT;
  const [picked, setPicked] = useState(current);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const orgValue = org.data?.data.settings[setting.key]?.value;
  const save = async () => {
    if (busy || picked === current) return;
    setBusy(true);
    setError(null);
    try {
      if (picked === INHERIT) await settingStore.removeOverride(branch.id, "catalogue", setting.key);
      else await settingStore.save(branch.id, "catalogue", { [setting.key]: picked === "on" });
      await qc.invalidateQueries({ queryKey: ["settings"] });
      onSaved(<><b>{setting.label}: {picked === INHERIT ? `${branch.name} follows the organisation again.` : `${picked === "on" ? "On" : "Off"} for ${branch.name}.`}</b> It applies within a minute to products added from then on; products already added keep their flags.</>);
    } catch (err) { setError(err); }
    finally { setBusy(false); }
  };
  return (
    <form className="row c gap-8" style={{ flexBasis: "100%", flexWrap: "wrap" }} onSubmit={(e) => { e.preventDefault(); void save(); }} aria-label={`Change ${setting.label}`}>
      <Select aria-label={setting.label} value={picked} autoFocus onChange={(e) => setPicked(e.target.value)}>
        <option value={INHERIT}>As the organisation ({org.data ? onOff(orgValue) : "…"})</option>
        <option value="on">On</option>
        <option value="off">Off</option>
      </Select>
      <button className="btn primary sm" type="submit" disabled={busy || picked === current}>{busy ? "Saving…" : "Save"}</button>
      <button className="btn sm" type="button" onClick={onClose}>Cancel</button>
      {error !== null && <div className="error" role="alert" style={{ flexBasis: "100%" }}>{describeError(error)}</div>}
    </form>
  );
}
